import { useTranslation } from 'react-i18next'
import { Home } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import { IconPicker } from '@/components/ui/icon-picker'
import { VisibilityPicker } from '@/components/ui/visibility-picker'
import { AccessGrantPicker } from '@/components/ui/access-grant-picker'
import { LockedDisplayPicker } from '@/components/ui/locked-display-picker'
import { generateSlug, typedSlug } from '@/lib/slug'
import { grantCount, type AccessGrants, type SectionLockedDisplay, type Visibility } from '@/types/access'

/** Everything a section is, as one object the dialogs hand back and forth. */
export interface SectionFormValues {
  icon: string
  title: string
  slug: string
  description: string
  visibility: Visibility
  access: AccessGrants
  isHome: boolean
  /** F-161a: what whoever has no access sees. */
  lockedDisplay: SectionLockedDisplay
}

interface SectionFormFieldsProps {
  values: SectionFormValues
  onChange: (values: SectionFormValues) => void
  disabled?: boolean
  communityId?: string
  /** How many spaces a stricter visibility would tighten along with it. */
  tightenCount?: number
  /**
   * Whether typing a title rewrites the slug.
   *
   * On for a new section, off for an existing one: a section that is already
   * linked from somewhere cannot have its address change because somebody fixed
   * a typo in its name.
   */
  autoSlug?: boolean
  /** Why the slug was refused (reserved word, leading `_`), shown under the field. */
  slugError?: string | null
}

/**
 * The body of both section dialogs.
 *
 * Creating and editing a section ask for the same seven things, and they used
 * to ask twice — two copies of the icon picker, the slug hint, the visibility
 * radios and the access column, drifting apart one fix at a time. The dialogs
 * keep what actually differs: their title, their submit, and what they do
 * afterwards.
 */
export function SectionFormFields({
  values,
  onChange,
  disabled,
  communityId,
  tightenCount = 0,
  autoSlug = false,
  slugError = null
}: SectionFormFieldsProps) {
  const { t } = useTranslation(['common'])

  const set = (patch: Partial<SectionFormValues>) => onChange({ ...values, ...patch })

  const setTitle = (title: string) => {
    /* Only while the slug is still the one the title produced: the moment
       somebody edits it by hand, it is theirs. */
    const slugFollows = autoSlug && (!values.slug || values.slug === generateSlug(values.title))
    set({ title, slug: slugFollows ? generateSlug(title) : values.slug })
  }

  return (
    <>
      <div className="space-y-4">
        <div className="grid grid-cols-[60px_1fr] gap-2">
          <div className="space-y-2">
            <label htmlFor="icon" className="text-sm font-medium">
              {t('common:fields.icon')}
            </label>
            <IconPicker
              value={values.icon}
              onChange={(icon) => set({ icon })}
              disabled={disabled}
              id="icon"
            />
          </div>

          <div className="space-y-2">
            <label htmlFor="title" className="text-sm font-medium">
              {t('common:fields.title')} *
            </label>
            <Input
              id="title"
              value={values.title}
              onChange={(event) => setTitle(event.target.value)}
              placeholder={t('common:sections.titlePlaceholder')}
              disabled={disabled}
              required
            />
          </div>
        </div>

        <div className="space-y-2">
          <label htmlFor="slug" className="text-sm font-medium">
            {t('common:fields.slug')}
          </label>
          <Input
            id="slug"
            value={values.slug}
            onChange={(event) => set({ slug: typedSlug(event.target.value) })}
            placeholder={t('common:sections.slugPlaceholder')}
            disabled={disabled}
            aria-invalid={!!slugError}
            className={slugError ? 'border-red-500 focus-visible:ring-red-500/20' : undefined}
          />
          {slugError
            ? <p className="text-xs text-red-600">{slugError}</p>
            : <p className="text-xs text-gray-500">{t('common:sections.slugHint')}</p>}
        </div>

        <div className="space-y-2">
          <label htmlFor="description" className="text-sm font-medium">
            {t('common:fields.description')}
          </label>
          <Textarea aiFieldKind="other"
            id="description"
            value={values.description}
            onChange={(event) => set({ description: event.target.value })}
            placeholder={t('common:sections.descriptionPlaceholder')}
            disabled={disabled}
            rows={3}
          />
        </div>
      </div>

      <VisibilityPicker
        value={values.visibility}
        onChange={(visibility) => set({ visibility })}
        disabled={disabled}
      />

      {tightenCount > 0 && (
        <p className="text-xs text-amber-600">
          {t('common:access.tightenWarning', { count: tightenCount })}
        </p>
      )}

      {values.visibility === 'private' && (
        <div className="rounded-lg border p-3">
          <AccessGrantPicker
            value={values.access}
            onChange={(access) => set({ access })}
            communityId={communityId}
            disabled={disabled}
          />
        </div>
      )}

      {/* F-161a: nobody is kept out of a public section, so there is nothing to choose. */}
      {values.visibility !== 'public' && (
        <LockedDisplayPicker
          kind="section"
          value={values.lockedDisplay}
          onChange={(lockedDisplay) => set({ lockedDisplay })}
          disabled={disabled}
          adminOnly={values.visibility === 'private' && grantCount(values.access) === 0}
        />
      )}

      <div className="border-t pt-4">
        <div className="flex items-start gap-4">
          <div className="mt-1 flex-shrink-0">
            <Switch
              id="isHome"
              checked={values.isHome}
              onCheckedChange={(isHome) => set({ isHome })}
              disabled={disabled}
            />
          </div>
          <div className="flex-1 space-y-1">
            <div className="flex items-center gap-2">
              <Home className="h-4 w-4 text-blue-600" />
              <label htmlFor="isHome" className="cursor-pointer text-sm font-medium">
                {t('common:sections.isHomeLabel')}
              </label>
            </div>
            <p className="text-xs leading-relaxed text-gray-500">
              {t('common:sections.isHomeDescription')}
            </p>
          </div>
        </div>
      </div>
    </>
  )
}
